/**
 * Privacy Content
 *
 * Public privacy page content rendered inside the shared legal shell.
 *
 * Primary responsibilities:
 * - Transform translated privacy content into structured sections
 * - Keep the privacy page aligned with the redesigned marketing shell
 *
 * Dependencies:
 * - legal-page-shell
 * - i18n
 */

'use client'

import { useTranslation } from 'react-i18next'

import '@/i18n'
import { LegalPageShell } from '@/components/marketing/legal-page-shell'
import { readTranslatedItems, readTranslatedText } from '@/components/marketing/legal-translation'

const fallbackPrivacyCopy = {
  backToHome: 'Back to Home',
  pageTitle: 'Privacy Policy',
  introText:
    'Your privacy is our top priority. We believe in complete transparency about how we collect, use, and protect your data.',
  sections: {
    dataCollection: {
      title: '1. Data Collection',
      intro: 'We collect only the minimum information necessary to provide our service:',
      items: [
        'Account information such as email and display name',
        'Chat conversations, files, and generated content you choose to store',
        'Usage data that helps us improve service performance',
        'Payment information processed securely by our payment provider'
      ]
    },
    dataUsage: {
      title: '2. How We Use Your Data',
      highlight: 'We never use your conversations or generated content to train AI models.',
      intro: 'Your data is used solely for:',
      items: [
        'Providing and maintaining the service',
        'Processing your requests and transactions',
        'Improving service performance and user experience',
        'Communicating service updates and account information'
      ]
    },
    security: {
      title: '3. Data Security',
      highlight: 'We implement enterprise-grade security measures to protect your data.',
      items: [
        'Encryption for sensitive data',
        'Regular security audits and updates',
        'Secure data storage with industry-standard protocols',
        'Strict access controls and authentication measures'
      ]
    },
    retention: {
      title: '4. Data Retention',
      text:
        'We retain your data only as long as necessary to provide our service or comply with legal obligations. You can request data deletion at any time.'
    },
    rights: {
      title: '5. Your Rights',
      intro: 'You have the right to:',
      items: [
        'Access your personal data',
        'Request data correction or deletion',
        'Export your data',
        'Opt out of non-essential communications'
      ]
    },
    thirdParty: {
      title: '6. Third-Party Services',
      text:
        'We use select third-party services for specific functions such as payment processing. These services are carefully vetted and bound by strict data protection agreements.'
    },
    updates: {
      title: '7. Updates to Privacy Policy',
      text:
        'We may update this policy periodically. We will notify you of significant changes and obtain consent where required by law.'
    },
    contact: {
      title: '8. Contact Us',
      text: 'For privacy-related inquiries, please contact us at privacy@marshmallow.studio'
    }
  }
} as const

export function PrivacyContent() {
  const { t } = useTranslation()

  const dataCollectionItems = readTranslatedItems(
    t('privacy.sections.data_collection.items', {
      returnObjects: true,
      defaultValue: fallbackPrivacyCopy.sections.dataCollection.items
    }),
    fallbackPrivacyCopy.sections.dataCollection.items
  )
  const dataUsageItems = readTranslatedItems(
    t('privacy.sections.data_usage.items', {
      returnObjects: true,
      defaultValue: fallbackPrivacyCopy.sections.dataUsage.items
    }),
    fallbackPrivacyCopy.sections.dataUsage.items
  )
  const securityItems = readTranslatedItems(
    t('privacy.sections.security.items', {
      returnObjects: true,
      defaultValue: fallbackPrivacyCopy.sections.security.items
    }),
    fallbackPrivacyCopy.sections.security.items
  )
  const rightsItems = readTranslatedItems(
    t('privacy.sections.rights.items', {
      returnObjects: true,
      defaultValue: fallbackPrivacyCopy.sections.rights.items
    }),
    fallbackPrivacyCopy.sections.rights.items
  )

  return (
    <LegalPageShell
      eyebrow="Privacy"
      title={readTranslatedText(
        t('privacy.page_title', { context: 'title', defaultValue: fallbackPrivacyCopy.pageTitle }),
        fallbackPrivacyCopy.pageTitle
      )}
      description={readTranslatedText(
        t('privacy.intro_text', { defaultValue: fallbackPrivacyCopy.introText }),
        fallbackPrivacyCopy.introText
      )}
      backLabel={readTranslatedText(
        t('privacy.back_to_home', { defaultValue: fallbackPrivacyCopy.backToHome }),
        fallbackPrivacyCopy.backToHome
      )}
      lastUpdated="October 29, 2026"
      sections={[
        {
          title: readTranslatedText(
            t('privacy.sections.data_collection.title', {
              defaultValue: fallbackPrivacyCopy.sections.dataCollection.title
            }),
            fallbackPrivacyCopy.sections.dataCollection.title
          ),
          intro: readTranslatedText(
            t('privacy.sections.data_collection.intro', {
              defaultValue: fallbackPrivacyCopy.sections.dataCollection.intro
            }),
            fallbackPrivacyCopy.sections.dataCollection.intro
          ),
          items: dataCollectionItems
        },
        {
          title: readTranslatedText(
            t('privacy.sections.data_usage.title', {
              defaultValue: fallbackPrivacyCopy.sections.dataUsage.title
            }),
            fallbackPrivacyCopy.sections.dataUsage.title
          ),
          highlight: readTranslatedText(
            t('privacy.sections.data_usage.highlight', {
              defaultValue: fallbackPrivacyCopy.sections.dataUsage.highlight
            }),
            fallbackPrivacyCopy.sections.dataUsage.highlight
          ),
          intro: readTranslatedText(
            t('privacy.sections.data_usage.intro', {
              defaultValue: fallbackPrivacyCopy.sections.dataUsage.intro
            }),
            fallbackPrivacyCopy.sections.dataUsage.intro
          ),
          items: dataUsageItems
        },
        {
          title: readTranslatedText(
            t('privacy.sections.security.title', {
              defaultValue: fallbackPrivacyCopy.sections.security.title
            }),
            fallbackPrivacyCopy.sections.security.title
          ),
          highlight: readTranslatedText(
            t('privacy.sections.security.highlight', {
              defaultValue: fallbackPrivacyCopy.sections.security.highlight
            }),
            fallbackPrivacyCopy.sections.security.highlight
          ),
          items: securityItems
        },
        {
          title: readTranslatedText(
            t('privacy.sections.retention.title', {
              defaultValue: fallbackPrivacyCopy.sections.retention.title
            }),
            fallbackPrivacyCopy.sections.retention.title
          ),
          body: readTranslatedText(
            t('privacy.sections.retention.text', {
              defaultValue: fallbackPrivacyCopy.sections.retention.text
            }),
            fallbackPrivacyCopy.sections.retention.text
          )
        },
        {
          title: readTranslatedText(
            t('privacy.sections.rights.title', {
              defaultValue: fallbackPrivacyCopy.sections.rights.title
            }),
            fallbackPrivacyCopy.sections.rights.title
          ),
          intro: readTranslatedText(
            t('privacy.sections.rights.intro', {
              defaultValue: fallbackPrivacyCopy.sections.rights.intro
            }),
            fallbackPrivacyCopy.sections.rights.intro
          ),
          items: rightsItems
        },
        {
          title: readTranslatedText(
            t('privacy.sections.third_party.title', {
              defaultValue: fallbackPrivacyCopy.sections.thirdParty.title
            }),
            fallbackPrivacyCopy.sections.thirdParty.title
          ),
          body: readTranslatedText(
            t('privacy.sections.third_party.text', {
              defaultValue: fallbackPrivacyCopy.sections.thirdParty.text
            }),
            fallbackPrivacyCopy.sections.thirdParty.text
          )
        },
        {
          title: readTranslatedText(
            t('privacy.sections.updates.title', {
              defaultValue: fallbackPrivacyCopy.sections.updates.title
            }),
            fallbackPrivacyCopy.sections.updates.title
          ),
          body: readTranslatedText(
            t('privacy.sections.updates.text', {
              defaultValue: fallbackPrivacyCopy.sections.updates.text
            }),
            fallbackPrivacyCopy.sections.updates.text
          )
        },
        {
          title: readTranslatedText(
            t('privacy.sections.contact.title', {
              defaultValue: fallbackPrivacyCopy.sections.contact.title
            }),
            fallbackPrivacyCopy.sections.contact.title
          ),
          body: readTranslatedText(
            t('privacy.sections.contact.text', {
              defaultValue: fallbackPrivacyCopy.sections.contact.text
            }),
            fallbackPrivacyCopy.sections.contact.text
          )
        }
      ]}
    />
  )
}
